import { Category, type ProviderFile } from "fileParsing/types";
import { v4 } from "uuid";
import { suggestDate } from "./date";
import { extractTemplatesFn } from "./extractTemplates";
import {
  type DataPointTemplate,
  type DataTemplate,
  EMPTY_SCHEMA,
  type Path,
  type SupposedUsage,
  type TimeUsage,
  type UnionType,
  Visibility,
  WILDCARD,
  type WranglerItem,
} from "./types";

const transformFn = (wranglerItem: WranglerItem, files: ProviderFile[]): WranglerItem => {
  console.log("3. Get List of Paths to DataArrays from Schema");
  // Get List of Paths to DataArrays from Schema
  // Transform list to DPTemplates
  const templates = extractTemplatesFn(wranglerItem, files).templates;

  console.log("4. Create data mapping for each path");
  // Majority vote over the files' guessed categories.
  // Counts by Category value directly: the old code looked the enum key up
  // from the value and then mapped back, which only differed from identity
  // for PostsAndComments ("Posts") and risked `undefined` keys.
  const votes = new Map<Category, number>();
  for (const file of wranglerItem.files) {
    const category = guessCategoryFromString(file.path);
    if (category === undefined) continue;
    votes.set(category, (votes.get(category) ?? 0) + 1);
  }

  const suggestCategory = Array.from(votes.entries()).sort(
    // Ties broken by category name for determinism across runs.
    ([ka, a], [kb, b]) => b - a || ka.localeCompare(kb),
  )[0]?.[0];

  const suggestSubcategory: DataTemplate[] = [
    // These empty text values are needed because its not edible otherwise
    { id: v4(), type: "text", value: "" },
    {
      id: v4(),
      type: "data",
      // TODO: remove "label"
      label: "labelFileName",
      // TODO: other idea instead of filename?
      value: ["labelFileName"],
    },
    { id: v4(), type: "text", value: "" },
  ];

  const decoratedTemplates = templates.map<DataPointTemplate>((t) => {
    const selectedTimestamps = getSuggestedValues(t.schema ?? EMPTY_SCHEMA, { format: "time" }, true)
      .map(({ path, usage }) => ({
        path: [...t.path, ...path],
        arguments: (usage as TimeUsage).argument ?? {},
        date: (usage as TimeUsage).sample?.getTime() ?? 0,
      }))
      .sort((b, a) => a.date - b.date)
      .map((e) => ({ path: e.path, arguments: e.arguments }));

    return {
      ...t,
      category: categoryFromPath(t.path) ?? suggestCategory,
      subcategory: suggestSubcategory,
      content: [
        { id: v4(), type: "text", value: "" },
        {
          id: v4(),
          type: "data",
          label: "Automatic",
          value: [...t.path, WILDCARD],
        },
        { id: v4(), type: "text", value: "" },
      ],
      selectedTimestamps,
      selectedUris: getSuggestedValues(t.schema ?? EMPTY_SCHEMA, {
        format: "uri",
      }).map(({ path }) => [...t.path, ...path]),
      selectedLatitudes: getSuggestedValues(t.schema ?? EMPTY_SCHEMA, {
        format: "lat",
      }).map(({ path }) => [...t.path, ...path]),
      selectedLongitudes: getSuggestedValues(t.schema ?? EMPTY_SCHEMA, {
        format: "long",
      }).map(({ path }) => [...t.path, ...path]),
      visibility: Visibility.visible,
    };
  });
  // A template earns visibility on its own merit, not by beating its siblings.
  // One file can hold dozens of unrelated data arrays (TikTok ships the whole
  // export as a single nested JSON), so "pick the best" silently drops ~everything.
  const isNested = (a: Path, b: Path): boolean => a.length > b.length && b.every((seg, ix) => seg === a[ix]);

  const withData = decoratedTemplates.filter((t) => t.dataPointsCount > 0 && t.selectedTimestamps.length > 0);

  // Prefer the outermost array of a nest: an inner array's rows are already
  // serialised into the outer row's text, so showing both double-counts.
  const visible = withData.filter((t) => !withData.some((other) => isNested(t.path, other.path)));

  // if nothing has a timestamp, we fall back to the single best
  // candidate so the item is not empty.
  const fallback =
    visible.length > 0
      ? visible
      : [
          decoratedTemplates.reduce<DataPointTemplate | undefined>(
            (champ, t) => (champ === undefined || t.dataPointsCount > champ.dataPointsCount ? t : champ),
            undefined,
          ),
        ].filter((t): t is DataPointTemplate => t !== undefined);

  const visibleSet = new Set(fallback);
  const templatesResult = decoratedTemplates.map((t) =>
    visibleSet.has(t) ? t : { ...t, visibility: Visibility.hidden },
  );

  return {
    ...wranglerItem,
    templates: templatesResult,
    category: suggestCategory,
    dataProgress: {
      ...wranglerItem.dataProgress,
      foundDataPoints: templates.length > 0,
      hasTimestamp: templatesResult.some((template) => template.selectedTimestamps.length > 0),
      guessedCategory: suggestCategory !== undefined,
    },
  };
};

const getSuggestedValues = (
  schema: UnionType,
  usage: SupposedUsage,
  strictLocal = false,
  templateDepth = true,
): { path: Path; usage: SupposedUsage }[] => {
  return schema.reduce<{ path: Path; usage: SupposedUsage }[]>((results, t) => {
    if (t.type === "array" && (!strictLocal || templateDepth)) {
      return [
        ...results,
        ...getSuggestedValues(t.anyOf, usage, strictLocal, false).map(({ path, usage }) => ({
          path: [WILDCARD, ...path],
          usage,
        })),
      ];
    } else if (t.type === "object") {
      return [
        ...results,
        ...Object.entries(t.properties).reduce<{ path: Path; usage: SupposedUsage }[]>((result, [k, v]) => {
          return [
            ...result,
            ...getSuggestedValues(v, usage, strictLocal, templateDepth).map(({ path, usage }) => ({
              path: [k, ...path],
              usage,
            })),
          ];
        }, []),
      ];
    }

    if (t.type === "number" || t.type === "string") {
      if (t.supposedUsage?.format === usage?.format) {
        return [...results, { path: [], usage: t.supposedUsage }];
      }
    }

    return results;
  }, []);
};

// We reuse the keyword table for the template's JSON path.
// In a single-file export (TikTok) the JSON path has the category info:
// ["Direct Message","Chat History", null] -> Messages.
const categoryFromPath = (path: Path): Category =>
  guessCategoryFromString(path.filter((s) => typeof s === "string").join("/"));

const tokenize = (str: string): string[][] =>
  str
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .split(/[/\\]/)
    .map((segment) => segment.split(/[^a-z\d]+/).filter((t) => t.length > 0));

const guessCategoryFromString = (str: string): Category | undefined => {
  const segments = tokenize(str);
  const scores = new Map<Category, number>();

  segments.forEach((tokens, segmentIx) => {
    const positionBonus = (segmentIx + 1) / segments.length; // 0 < bonus <= 1
    tokens.forEach((token) => {
      KEYWORDS.forEach(([keyword, category, weight]) => {
        // Prefix match
        if (!token.startsWith(keyword) && !keyword.startsWith(token)) return;
        // Partial matches are scored less
        const exactness = token === keyword ? 1 : 0.6;
        const score = weight * exactness * positionBonus;
        scores.set(category, Math.max(scores.get(category) ?? 0, score));
      });
    });
  });

  return Array.from(scores.entries()).sort(([, a], [, b]) => b - a)[0]?.[0];
};

const KEYWORDS: [string, Category, number][] = [
  ["like", Category.Likes, 2],
  ["favorite", Category.Likes, 2],
  ["favourite", Category.Likes, 2],
  ["reaction", Category.Likes, 2],

  ["comment", Category.PostsAndComments, 2],
  ["post", Category.PostsAndComments, 2],
  ["tweet", Category.PostsAndComments, 2],
  ["story", Category.PostsAndComments, 2],
  ["reply", Category.PostsAndComments, 2],

  ["directmessage", Category.Messages, 3],
  ["message", Category.Messages, 2],
  ["chat", Category.Messages, 2],
  ["mail", Category.Messages, 2],
  ["conversation", Category.Messages, 2],
  ["inbox", Category.Messages, 2],
  ["notification", Category.Messages, 1],

  ["location", Category.Location, 3],
  ["geo", Category.Location, 2],
  ["gps", Category.Location, 3],
  ["place", Category.Location, 2],
  ["position", Category.Location, 2],
  ["address", Category.Location, 1],

  ["contact", Category.Contacts, 2],
  ["friend", Category.Contacts, 2],
  ["follower", Category.Contacts, 2],
  ["following", Category.Contacts, 2],
  ["connection", Category.Contacts, 2],
  ["block", Category.Contacts, 2],

  ["profile", Category.Account, 2],
  ["account", Category.Account, 2],
  ["subscription", Category.Account, 2],
  ["transaction", Category.Account, 2],
  ["payment", Category.Account, 2],
  ["preference", Category.Account, 2],
  ["setting", Category.Account, 2],
  ["family", Category.Account, 1],

  ["photo", Category.Media, 2],
  ["video", Category.Media, 2],
  ["image", Category.Media, 2],
  ["img", Category.Media, 2],
  ["media", Category.Media, 2],
  ["music", Category.Media, 2],
  ["audio", Category.Media, 2],
  ["podcast", Category.Media, 2],

  ["login", Category.Security, 3],
  ["loginhistory", Category.Security, 3],
  ["security", Category.Security, 3],
  ["password", Category.Security, 3],
  ["device", Category.Security, 2],
  ["session", Category.Security, 2],
  ["ipaddress", Category.Security, 2],
  ["twofactor", Category.Security, 3],

  ["video browsing history", Category.Activity, 4],
  ["share history", Category.Activity, 4],
  ["browsing history", Category.Activity, 4],
  ["searchhistory", Category.Activity, 3],
  ["watchhistory", Category.Activity, 3],
  ["search", Category.Activity, 2],
  ["activity", Category.Activity, 1],
  ["engagement", Category.Activity, 1],
  ["interaction", Category.Activity, 1],
  ["advertis", Category.Activity, 2],
  ["history", Category.Activity, 1],
] as const;

export { suggestDate, transformFn };
